/**
 * ticket-inapp.styles — every layout and type rule the in-app card markup
 * needs, carried as co-located CSS the card emits for itself.
 *
 * WHY NOT TAILWIND UTILITIES: a consumer app's Tailwind output contains only
 * the classes its own content scan found, and that scan reaches a workspace
 * package only when the package is discoverable from the app's own
 * `node_modules` — which is a function of the app's dependency manifest, not
 * of what the app actually renders. An app that mounts this card through an
 * intermediate package therefore compiles none of the card's utilities: the
 * display wordmark's per-character boxes never apply (its fit-to-box
 * correction has nothing to transform, so a long name overruns its box),
 * the QR frame's fill and radius never apply (the frame disappears, leaving a
 * bare plate), and the card silently renders as a different composition than
 * the one that was designed and reviewed. No error is raised and no build
 * fails — the classes simply are not in the stylesheet. `ticket-inapp.palette.ts`
 * already carries the grounds as co-located CSS on the narrower version of
 * this reasoning (an ARBITRARY utility used only here is never emitted); the
 * same argument covers every utility the card uses, arbitrary or not, because
 * discoverability rather than arbitrariness is what decides whether a class is
 * compiled.
 *
 * These rules are therefore the card's own, resolved by the document that
 * mounts it with no build step of the host's involved. Every class is
 * `tkt-`-prefixed so nothing here can collide with, or be overridden by, a
 * host's own stylesheet.
 *
 * THE WORDMARK RULES ARE SHARED BY EVERY VARIANT that renders a display
 * wordmark (`renderWordmark`, ticket-inapp.stencil-wordmark.ts), so they live
 * here rather than beside any one composition.
 *
 * ONE DECLARATION STAYS ON THE HOST THEME — the hairline around the card's
 * outer edge, for the reason ticket-inapp.palette.ts states: that border
 * separates the card from the page behind it, so it is the one edge whose
 * correct value depends on the surface the card was dropped onto. It resolves
 * through the public schmancy token with a literal fallback, so a host that
 * defines no theme still gets a visible edge rather than none.
 */
import { html, type TemplateResult } from "lit";
import {
	STENCIL_LABEL_OPACITY,
	STENCIL_VALUE_OPACITY,
} from "./ticket.palette.ts";
import {
	TICKET_CARD_DISPLAY_FAMILY,
	TICKET_CARD_TEXT_FAMILY,
} from "./ticket-inapp.fonts.ts";

/** The stencil footer's own stack gap and the line box its tagline occupies,
 *  declared here — where the rules that spend them are — and read back by the
 *  composition (ticket-inapp.stencil.template.ts) to size an uploaded brand
 *  mark to the exact envelope the generated wordmark and tagline would have
 *  spent. A second copy of either number beside that arithmetic is what would
 *  let the footer's height differ between the two forms, and the footer's
 *  height is what decides how much of the card's fixed 576px the QR gets. */
export const STENCIL_FOOTER_GAP_PX = 4;
export const STENCIL_TAGLINE_LINE_PX = 14;

export const ticketCardStyles: TemplateResult = html`
  <style>
    /* Display wordmark — every character is its own inline-block box so a
       transform applies to it at all (transform has no effect on a
       non-replaced inline box), and each WORD is a nowrap box so the run's
       only break opportunities are the real spaces between words. The flip
       rule is the target of WORDMARK_FLIPS_GLYPHS
       (ticket-inapp.stencil-wordmark.ts), which owns whether any character
       carries that class. */
    .tkt-wordmark-word {
      display: inline-block;
      white-space: nowrap;
    }
    .tkt-wordmark-char {
      display: inline-block;
    }
    .tkt-wordmark-char--flip {
      display: inline-block;
      transform: scaleX(-1);
    }

    /* The measured run a fit-to-box correction scales — inline-block so a
       transform applies to it at all, the same reason each wordmark
       character above needs its own box. */
    .tkt-fit-run {
      display: inline-block;
    }

    .tkt-card-display {
      font-family: "${TICKET_CARD_DISPLAY_FAMILY}", ui-sans-serif, sans-serif;
      letter-spacing: -0.09em;
    }
    .tkt-card-text {
      font-family: "${TICKET_CARD_TEXT_FAMILY}", ui-sans-serif, system-ui, sans-serif;
    }

    /* QR credential hero -------------------------------------------------- */
    .tkt-qr-stack {
      display: flex;
      width: 100%;
      flex-direction: column;
      gap: 12px;
    }
    .tkt-qr-plate {
      border-radius: 16px;
      background-color: #ffffff;
    }
    .tkt-qr-plate--lg {
      padding: 12px;
    }
    .tkt-qr-plate--xl {
      padding: 16px;
    }
    .tkt-align-start {
      align-items: flex-start;
      text-align: left;
    }
    .tkt-align-center {
      align-items: center;
      text-align: center;
    }
    .tkt-align-end {
      align-items: flex-end;
      text-align: right;
    }

    /* Shared across every variant ---------------------------------------- */

    /* The card's outer box, identical for all three compositions — and the
       one stable hook a caller or a test uses to find the card root, which
       an inner ground class is not (every variant paints several). */
    .tkt-card {
      margin-inline: auto;
      width: 100%;
      max-width: 384px;
      aspect-ratio: 2 / 3;
      overflow: hidden;
      border-radius: 24px;
      border: 1px solid
        var(--schmancy-sys-color-outlineVariant, rgba(255, 255, 255, 0.24));
      box-sizing: border-box;
    }
    .tkt-card--column {
      display: flex;
      flex-direction: column;
    }
    .tkt-card--raised {
      box-shadow:
        0 10px 15px -3px rgb(0 0 0 / 0.1),
        0 4px 6px -4px rgb(0 0 0 / 0.1);
    }

    /* Inherited, so it reaches a schmancy-typography's own shadow text. */
    .tkt-dim {
      opacity: 0.7;
    }
    .tkt-track-widest {
      letter-spacing: 0.1em;
    }
    .tkt-track-wide {
      letter-spacing: 0.025em;
    }
    .tkt-mono {
      font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas,
        "Liberation Mono", "Courier New", monospace;
    }
    .tkt-full-width {
      width: 100%;
    }

    /* Editorial composition ---------------------------------------------- */
    .tkt-ed-fill {
      height: 100%;
    }
    .tkt-ed-stack {
      display: flex;
      flex-direction: column;
      gap: 4px;
    }
    .tkt-ed-title {
      line-height: 0.95;
    }

    /* Strata composition -------------------------------------------------- */
    .tkt-st-identity {
      display: flex;
      flex-direction: column;
      gap: 8px;
      padding: 24px 32px;
    }
    .tkt-st-title {
      line-height: 1.25;
    }
    .tkt-st-when {
      display: flex;
      flex-direction: column;
      gap: 4px;
      padding-top: 8px;
    }
    .tkt-st-band {
      display: flex;
      min-height: 0;
      flex: 1 1 0%;
      flex-direction: column;
      align-items: flex-start;
      gap: 16px;
      padding: 24px 32px;
    }

    /* Stencil composition ------------------------------------------------ */
    /* Only what differs from the shared .tkt-card box above — this
       composition stacks its sections itself, so it owns the flow and the
       padding and inherits every other outer-box property. */
    .tkt-stencil-card {
      display: flex;
      flex-direction: column;
      gap: 16px;
      padding: 24px 24px 20px;
    }
    .tkt-stencil-logo {
      height: 74px;
      width: auto;
      max-width: 100%;
      margin-bottom: 24px;
      flex-shrink: 0;
      align-self: flex-start;
      object-fit: contain;
      object-position: left;
    }
    .tkt-stencil-wordmark {
      margin-bottom: 24px;
      flex-shrink: 0;
    }
    .tkt-stencil-rows {
      display: flex;
      flex-shrink: 0;
      flex-direction: column;
      gap: 24px;
    }
    .tkt-stencil-row {
      display: grid;
      grid-template-columns: 1.3fr 1fr;
      gap: 16px;
    }
    .tkt-stencil-cell {
      display: flex;
      min-width: 0;
      flex-direction: column;
      gap: 4px;
    }
    .tkt-stencil-label {
      font-size: 10px;
      font-weight: 700;
      letter-spacing: 0.5px;
      line-height: 14px;
      text-transform: uppercase;
      margin: 0;
      opacity: ${STENCIL_LABEL_OPACITY};
    }
    .tkt-stencil-value {
      opacity: ${STENCIL_VALUE_OPACITY};
    }
    .tkt-stencil-value--mono {
      font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas,
        "Liberation Mono", "Courier New", monospace;
    }
    /* The credential takes every pixel the card's other sections leave it:
       the slot claims the column's surplus (flex: 1 1 0%), the frame takes
       the slot's whole height, and the plate is whatever square that height
       leaves once the caption below it is served — so the QR's side is
       min(surplus height, the card's content width) with no pixel size
       stated anywhere. min-height: 0 on the slot is load-bearing the same
       way it is on editorial's own grid items: without it the slot's content
       min-size, not the surplus, would set its height and push the footer
       past the card's edge. */
    .tkt-stencil-qr-slot {
      display: flex;
      min-height: 0;
      flex: 1 1 0%;
      align-items: center;
      justify-content: center;
    }
    .tkt-stencil-qr-frame {
      /* The plate's caption and the gap above it — the one part of the
         frame's height that is NOT the square, stated here beside the two
         declarations it sums (gap below, line-height further down) and read
         back by the plate's own height. */
      --tkt-stencil-qr-caption-space: 22px;
      display: flex;
      /* Fills the slot, capped so a card with a lot of surplus grows a
         square bounded by its own gutters (the caller states the content
         width in --tkt-stencil-qr-max-width) rather than a black band taller
         than it is wide. Capped, the frame is shorter than the slot and the
         slot's own align-items centres it. */
      height: 100%;
      max-width: var(--tkt-stencil-qr-max-width);
      max-height: calc(
        var(--tkt-stencil-qr-max-width) + var(--tkt-stencil-qr-caption-space)
      );
      box-sizing: border-box;
      flex-direction: column;
      align-items: center;
      gap: 8px;
      padding: 6px;
      border-radius: 16px;
      background-color: #000000;
    }
    .tkt-stencil-qr-plate {
      /* A definite height off the frame's own, and a square by ratio rather
         than by a second length — so the code's side follows the height the
         card has left and its width can never disagree with it. */
      height: calc(100% - var(--tkt-stencil-qr-caption-space));
      aspect-ratio: 1 / 1;
      border-radius: 12px;
      background-color: #ffffff;
      padding: 8px;
      box-sizing: border-box;
    }
    .tkt-stencil-qr-caption {
      /* Never the section that gives way: sub-pixel rounding in the frame's
         height is absorbed by the plate above, which shrinks squarely. */
      flex: none;
      font-size: 10px;
      font-weight: 500;
      letter-spacing: 0.5px;
      line-height: 14px;
      text-transform: uppercase;
      color: rgba(255, 255, 255, 0.7);
      margin: 0;
    }
    .tkt-stencil-footer {
      display: flex;
      min-width: 0;
      flex-shrink: 0;
      flex-direction: column;
      align-items: center;
      gap: ${STENCIL_FOOTER_GAP_PX}px;
      text-align: center;
    }
    /* The operator's own uploaded mark, in place of the generated wordmark.
       Its height arrives inline (the composition works out the envelope the
       generated form would have spent, which moves with the hero's tier and
       with whether the brand name carries a tagline), so what stays here is
       what bounds the OTHER axis, forbids a crop, and sets the ink.

       A width capped at the footer's own plus object-fit: contain is the
       no-crop pair: contain letterboxes a mark too wide for that cap inside
       the height rather than cutting it, where object-fit: cover would cut
       it and is never correct for a wordmark. flex-shrink: 0 keeps the
       column's own shrinking off it.

       THE MARK TAKES THE CARD'S INK RATHER THAN ITS OWN TONES: brightness(0)
       collapses every colour channel to black and invert(1) lifts that to
       white, neither touching alpha — so whatever the operator authored,
       what lands here is their mark's own silhouette in the same white this
       card sets every other glyph in. That is what removes the choice
       between a dark asset and a light one; the operator uploads one file
       and it reads on this ground either way. The pair is written for THIS
       ground's ink specifically (TICKET_GROUNDS.stencil.ink, white), which
       is why the rule is stencil-scoped rather than shared.

       It needs the mark to carry an alpha channel, which every png/webp
       wordmark does and a jpeg does not: a jpeg's opaque field inverts to a
       solid white block. */
    .tkt-stencil-brand-logo {
      max-width: 100%;
      flex-shrink: 0;
      object-fit: contain;
      object-position: center;
      filter: brightness(0) invert(1);
    }
    .tkt-stencil-footer-label {
      font-size: 10px;
      font-weight: 400;
      letter-spacing: 0.5px;
      line-height: 14px;
      text-transform: uppercase;
      opacity: 0.7;
      margin: 0;
    }
    .tkt-stencil-tagline {
      font-size: 10px;
      font-weight: 400;
      letter-spacing: 0.5px;
      line-height: ${STENCIL_TAGLINE_LINE_PX}px;
      text-transform: uppercase;
      opacity: 0.7;
      overflow-wrap: anywhere;
      margin: 0;
    }
  </style>
`;
